import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs } from '../../components/primitives';
import { MagentoOperationsPanel } from '../../components/project/MagentoOperationsPanel';
import { EmptyState, ErrorNotice, LoadingRows, StatusBadge } from '../../components/ui';
import { EnvironmentHubCard } from '../../components/EnvironmentHub';
import { ReadinessPanel } from '../../components/EnvironmentReadiness';
import type { ReadinessState } from '../../components/EnvironmentReadiness';
import { MagentoTopologyCard } from '../../components/MagentoTopologyCard';
import { CatalogSearchCard } from '../../components/CatalogSearchCard';
import { VarnishCacheCard } from '../../components/VarnishCacheCard';
import { StorefrontCard } from '../../components/StorefrontCard';
import { ExecutionRunView } from '../../components/ExecutionRunView';
import { PanelCard, ProjectSection, QuietEmpty, SectionHeader } from '../../components/project/ui';
import { relativeTime } from '../../components/project/projectHealth';
import { useProject } from './ProjectContext';
import { EnvironmentEntryPoint } from '../../components/server/ServerEdgeSection';
import { useEffect, useMemo } from 'react';
import { RuntimePanel } from '../../components/project/RuntimePanel';
import { getEnvironmentRuntime } from '../../api';
import { useResource } from '../../hooks';

/**
 * InfrastructureSection is where the environment's machine-level truth
 * lives: the server it runs on, what runs there, the resources it uses and
 * who owns them, the storefront map, and the readiness ledger in full.
 *
 * Everything the setup flow discovered stays reachable here once setup is
 * over — the wizard disappears, its findings do not.
 */
type InfrastructureView = 'server' | 'storefronts' | 'operations';

export function InfrastructureSection({ readiness }: { readiness: ReadinessState }) {
  const project = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const { environment, environmentOverview, sectionPath } = project;
  const environmentId = environment?.id;
  const runtime = useResource(
    () => (environmentId ? getEnvironmentRuntime(environmentId) : Promise.resolve(undefined)),
    [environmentId],
    environmentId,
    { key: `env:${environmentId}:runtime` },
  );
  const openRunId = searchParams.get('run');
  const { hash } = useLocation();
  const navigate = useNavigate();
  // Two views (audit G4): the machine and its services, and what the shops
  // answer on. "#storefronts" links from readiness and the Overview land on
  // the second.
  const view: InfrastructureView =
    searchParams.get('view') === 'storefronts' || hash === '#storefronts' ? 'storefronts' : searchParams.get('view') === 'operations' ? 'operations' : 'server';
  const setView = (next: InfrastructureView) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'server') params.delete('view');
    else params.set('view', next);
    navigate({ search: params.toString(), hash: '' }, { replace: true });
  };

  // "Add a domain" in the readiness ledger (here, on the Overview and in
  // the header) lands on the storefront map, where hostnames are added.
  useEffect(() => {
    if (!hash || openRunId) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    const frame = window.requestAnimationFrame(() => target.scrollIntoView?.({ block: 'start' }));
    return () => window.cancelAnimationFrame(frame);
  }, [hash, openRunId, environment?.id]);

  // "Check readiness again" from an incident's suggested actions (UX-01):
  // re-run the readiness checks and bring the ledger into view.
  const readinessIntent = searchParams.get('intent') === 'readiness';
  useEffect(() => {
    if (!readinessIntent || openRunId || view !== 'server') return;
    readiness.reload();
    // The ledger mounts once the environment's data is in: try for a moment,
    // and consume the intent only then (consuming it re-runs this effect).
    let frame = 0;
    let tries = 0;
    const consume = () => {
      const params = new URLSearchParams(searchParams);
      params.delete('intent');
      setSearchParams(params, { replace: true });
    };
    const focusLedger = () => {
      const heading = document.getElementById(`readiness-${environment?.id}`);
      if (heading) {
        heading.scrollIntoView({ block: 'start' });
        heading.focus();
        consume();
      } else if (tries++ < 120) {
        frame = window.requestAnimationFrame(focusLedger);
      } else {
        consume();
      }
    };
    frame = window.requestAnimationFrame(focusLedger);
    return () => window.cancelAnimationFrame(frame);
    // Runs once per intent; readiness.reload is stable per state object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readinessIntent, openRunId, view, environment?.id]);

  const releaseTags = useMemo(() => {
    const tags: Record<string, string> = {};
    for (const deployment of project.deployments.data?.deployments ?? []) {
      tags[deployment.release_id] = deployment.source_commit_sha.slice(0, 7);
      if (deployment.frontend_release_id && deployment.frontend_commit_sha) {
        tags[deployment.frontend_release_id] = deployment.frontend_commit_sha.slice(0, 7);
      }
    }
    return tags;
  }, [project.deployments.data]);

  if (!environment) {
    return (
      <ProjectSection>
        <EmptyState
          title="No environment selected"
          description="Add an environment in Settings."
        />
      </ProjectSection>
    );
  }

  if (openRunId) {
    return (
      <ProjectSection>
        <ExecutionRunView
          executionId={openRunId}
          sectionPath={project.sectionPath}
          onSettled={project.refresh}
          onBack={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('run');
            setSearchParams(next, { replace: true });
            project.refresh();
          }}
        />
      </ProjectSection>
    );
  }

  const placements = environmentOverview.data?.placements ?? [];
  const serverId = placements[0]?.server_id;
  const server = project.servers.data?.servers?.find((candidate) => candidate.id === serverId);

  if (placements.length === 0) {
    return (
      <ProjectSection>
        <PanelCard labelledBy="infra-empty" testId="infrastructure-not-set-up">
          <SectionHeader
            id="infra-empty"
            title="Not on a server yet"
            actions={
              <Link className="button button--primary" to={sectionPath('setup')}>
                Continue setup
              </Link>
            }
          />
        </PanelCard>
      </ProjectSection>
    );
  }

  return (
    <ProjectSection>
      <Tabs
        label="Infrastructure views"
        variant="secondary"
        idBase="infrastructure-view"
        panelId="infrastructure-panel"
        value={view}
        onChange={setView}
        options={[
          { value: 'server', label: 'Server and services', testId: 'infrastructure-view-server' },
          { value: 'storefronts', label: 'Storefronts and domains', testId: 'infrastructure-view-storefronts' },
          { value: 'operations', label: 'Magento operations', testId: 'infrastructure-view-operations' },
        ]}
      />
      <div className="project-section" role="tabpanel" id="infrastructure-panel" aria-labelledby={`infrastructure-view-${view}`}>
        {view === 'server' ? (
          <>
            {/* The verdict leads (CO-184): is this environment safe to leave alone,
                and what exactly needs doing. The shell already holds the
                readiness state for the header and the Overview; the panel
                renders it rather than reading it again. */}
            <ReadinessPanel environmentId={environment.id} state={readiness} />

            <ErrorNotice error={environmentOverview.error} />

            <EnvironmentHubCard
              environment={environment}
              overview={environmentOverview.data}
              loading={environmentOverview.loading}
              error={environmentOverview.error}
              servers={project.servers.data?.servers ?? []}
              applications={project.applications}
              releaseTags={releaseTags}
              onRefresh={project.refresh}
            />

            <PanelCard labelledBy="infra-summary" testId="infrastructure-summary">
              {/* Static facts, on demand: the live verdict is the readiness
                  ledger above (CO-184). */}
              <details className="disclosure" data-testid="infrastructure-details">
                <summary id="infra-summary">Server details</summary>
                <div className="infrastructure-summary">
                  {project.servers.loading && !server ? (
                    <LoadingRows label="Loading server" rows={2} rowHeight="22px" />
                  ) : server ? (
                    <dl className="settings-facts" aria-label="Server" data-testid="infrastructure-server">
                      <div>
                        <dt>Server</dt>
                        <dd>
                          <Link to={`/app/servers/${server.id}`}>{server.name}</Link>
                        </dd>
                      </div>
                      <div>
                        <dt>Terminal</dt>
                        <dd>
                          <Link to={`/app/servers/${server.id}#terminal`}>Open</Link>
                        </dd>
                      </div>
                      <div>
                        <dt>Hostname</dt>
                        <dd>{server.hostname ? <code>{server.hostname}</code> : <span className="muted">Not known</span>}</dd>
                      </div>
                      <div>
                        <dt>Agent</dt>
                        <dd className="cell-inline">
                          <StatusBadge kind="serverAgent" value={server.status} />
                          {server.agent_version ? <code translate="no">v{server.agent_version}</code> : null}
                        </dd>
                      </div>
                      <div>
                        <dt>Server check</dt>
                        <dd>
                          <StatusBadge kind="serverCheck" value={server.readiness_status} />
                        </dd>
                      </div>
                      <div>
                        <dt>Web entry point</dt>
                        <dd>
                          <EnvironmentEntryPoint serverId={server.id} serverName={server.name} environmentId={environment.id} />
                        </dd>
                      </div>
                      <div>
                        <dt>Connected</dt>
                        <dd>{relativeTime(server.created_at)}</dd>
                      </div>
                    </dl>
                  ) : (
                    <QuietEmpty>The server record for this placement could not be read.</QuietEmpty>
                  )}
                  <dl className="settings-facts" aria-label="Runtime" data-testid="infrastructure-runtime">
                    <div>
                      <dt>Deployed as</dt>
                      <dd>{environment.deployment_mode === 'managed_docker' ? 'Commerce Ops managed stack' : 'Existing Magento server (SSH)'}</dd>
                    </div>
                    <div>
                      <dt>Magento</dt>
                      <dd>
                        {project.plan.data?.repository_analysis?.magento_version ||
                          project.plan.data?.repository_analysis?.magento_version_constraint || <span className="muted">Not analysed yet</span>}
                      </dd>
                    </div>
                    <div>
                      <dt>PHP required</dt>
                      <dd>{project.plan.data?.repository_analysis?.required_php_version || <span className="muted">Not analysed yet</span>}</dd>
                    </div>
                    <div>
                      <dt>Architecture</dt>
                      <dd>{project.isHeadless ? 'Headless: Magento plus separate storefronts' : 'Magento renders its own storefront'}</dd>
                    </div>
                    <div>
                      <dt>After a failed deployment</dt>
                      <dd>
                        {environment.rollback_policy === 'automatic_code_rollback_when_safe'
                          ? 'Roll back automatically when the database allows'
                          : 'Never roll back automatically'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </details>
            </PanelCard>

            {/* Each component's runtime (blue-green): what serves, what is kept
                for rollback, background work. Its home is here since CO-150;
                the Overview shows what each component serves. */}
            {project.setup.complete ? (
              <RuntimePanel
                runtime={runtime.data?.runtime}
                loading={runtime.loading}
                environmentId={environment.id}
                settingsPath={project.sectionPath('settings')}
              />
            ) : null}

            {/* What can be changed sits apart from what is (CO-161). */}
            <h2 className="project-subheading" id="infrastructure-configuration">
              Configuration
            </h2>

            {/* Luma, Hyvä or headless: what deployments build and check. */}
            {project.applications.some((a) => a.type === 'magento_backend') ? <StorefrontCard key={`storefront-${project.projectId}`} projectId={project.projectId} /> : null}

            <CatalogSearchCard
              key={`catalog-search-${environment.id}`}
              projectId={project.projectId}
              environmentId={environment.id}
              applications={project.applications}
              canInspect={placements.length > 0}
            />

            {/* Varnish is part of a traditional Magento's stack only; the card
                renders nothing for a headless project (CO-127). */}
            {!project.isHeadless ? (
              <VarnishCacheCard
                key={`varnish-${environment.id}`}
                environmentId={environment.id}
                setupPath={`/app/projects/${project.projectId}/setup?env=${encodeURIComponent(environment.slug)}`}
                onExecutionStarted={(id) => setSearchParams(new URLSearchParams({ env: environment.slug, run: id }))}
              />
            ) : null}
          </>
        ) : view === 'operations' ? (
          <MagentoOperationsPanel
            key={`operations-${environment.id}`}
            environmentId={environment.id}
            onExecutionStarted={(id) => setSearchParams(new URLSearchParams({ env: environment.slug, run: id }))}
          />
        ) : (
          <div id="storefronts" className="scroll-anchor">
            <MagentoTopologyCard
              key={`topology-${environment.id}`}
              environmentId={environment.id}
              applications={project.applications}
              onExecutionStarted={(id) => setSearchParams(new URLSearchParams({ env: environment.slug, run: id }))}
            />
          </div>
        )}
      </div>
    </ProjectSection>
  );
}
